import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAsync } from 'react-async';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import uuidV4 from 'uuid/v4';
import { requester } from '../api/config';
import colors from '../colors.json';
import Icon from '../components/Icon';
import { MODALS, PAYSTACK_REFERENCE_PREFIX } from '../constants';
import GlobalLoaderContext from '../contexts/global-loader';
import ModalContext from '../contexts/modal';
import PageDataContext from '../contexts/page-data';
import routes, {
  ModalMethodsOptions,
  ModalOptions,
  ModalState,
  registerModalRoute
} from '../routes';
import formsActions from '../store/modules/forms/actions';
import { getFormInfo } from '../store/modules/forms/reducer';
import { getUser } from '../store/modules/pages/reducer';
import targetsActions from '../store/modules/targets/actions';
import { getTargetPageData } from '../store/modules/targets/reducer';
import {
  ApiPageComponent,
  ApiPageData,
  FlatCardResponse,
  PayStackCallback,
  TransactionResponse
} from '../types';
import { BaseTargetPageLinkData } from '../views/Dashboard/SavingsView/SavingsTargetsView/SavingsTargetsListView/shared/TargetIconActionPageLink';
import {
  appendPageDataToPageId,
  appendPageIdToUrl,
  formatToCurrency,
  getRelativePeriod,
  isURL,
  parseQueryString,
  refreshPage
} from './utils';

export const useSectionFooterLinks = (actionIcons: any[]) => {
  const mapIcon = useCallback(
    ({ name, color, type = 'fa' }) => (props: any) => (
      <Icon type={type} name={name} color={color} {...props} />
    ),
    []
  );
  return useMemo(
    () =>
      actionIcons.map(
        ({ title: text, icon, iconColor, type, link, linkData, ...rest }) => ({
          text,
          icon: mapIcon({
            name: icon,
            color: iconColor,
            type
          }),
          style: { color: iconColor },
          link: link.link || link,
          linkData: link.data || linkData,
          ...rest
        })
      ),
    [mapIcon, actionIcons]
  );
};

export const useRouteLink = () => {
  const match = useRouteMatch();
  return useCallback(
    (
      link: string = '',
      linkData: { [key: string]: any } = {},
      methods: ModalMethodsOptions = {}
    ) => {
      const [routeId, data] = parseQueryString(link);
      const state: ModalState = {
        routeId,
        ...data,
        ...linkData
      };
      if (isURL(routeId)) {
        return {
          pathname: link,
          state
        };
      }
      if (routeId in routes) {
        return {
          ...routes[routeId],
          state: {
            ...state,
            ...routes[routeId].state
          },
          methods
        };
      }
      if (!('pageId' in state) && !('formId' in state)) {
        return {
          state,
          methods
        };
      }
      let pathname;
      if (state.pageId) {
        pathname = appendPageIdToUrl(
          match.url,
          appendPageDataToPageId(state.pageId, state)
        );
      } else if (state.formId) {
        pathname = appendPageIdToUrl(match.url, state.formId);
      }
      state.modal = true;
      return {
        pathname,
        state,
        methods
      };
    },
    [match.url]
  );
};

export const useIds = (length: number): string[] => {
  const [ids, setIds] = useState(Array.from({ length }).map(() => uuidV4()));
  useEffect(() => {
    if (length !== ids.length) {
      setIds(Array.from({ length }).map(() => uuidV4()));
    }
  }, [length, ids.length]);
  return ids;
};

export type ApiPageResponse = { pagedata: ApiPageData };

export const useModalAttrs = ({
  initialHeaderTitle = '',
  initialHeaderDescription = '',
  pageInfo
}: {
  initialHeaderTitle?: string;
  initialHeaderDescription?: string;
  pageInfo?: ApiPageResponse;
}) => {
  const extractModalAttrs = useCallback((_pageInfo: ApiPageResponse): [
    string,
    string,
    ApiPageData
  ] => {
    const { pagedata } = _pageInfo;
    const headerTexts = [];
    let index = 0;
    while (
      pagedata.components[index] &&
      pagedata.components[index].component === 'text' &&
      index < 2
    ) {
      headerTexts.push(pagedata.components[index].text);
      index += 1;
    }
    let headerTitle;
    let headerDescription;
    [headerTitle, headerDescription] = headerTexts;
    const formComponentData = pagedata.components.find(
      ({ component }: ApiPageComponent) => component === 'form'
    );
    if (formComponentData && formComponentData.formHeader) {
      ({
        title: headerTitle,
        subTitle: headerDescription
      } = formComponentData.formHeader);
    }
    const pageData = {
      ...pagedata,
      components: pagedata.components.slice(index)
    };
    return [headerTitle, headerDescription, pageData];
  }, []);
  const [headerTitle, setHeaderTitle] = useState(initialHeaderTitle);
  const [headerDescription, setHeaderDescription] = useState(
    initialHeaderDescription
  );
  const [pageData, setPageData] = useState<ApiPageData | null>(null);
  useEffect(() => {
    if (!pageInfo) {
      return;
    }
    const [_headerTitle, _headerDescription, _pageData] = extractModalAttrs(
      pageInfo
    );
    setHeaderTitle(_headerTitle ?? '');
    setHeaderDescription(_headerDescription ?? '');
    setPageData(_pageData || null);
  }, [pageInfo, extractModalAttrs]);

  return useMemo(
    () => ({
      headerTitle,
      headerDescription,
      pageData,
      extractModalAttrs,
      setHeaderTitle,
      setHeaderDescription
    }),
    [
      headerTitle,
      headerDescription,
      pageData,
      extractModalAttrs,
      setHeaderTitle,
      setHeaderDescription
    ]
  );
};

export const useFormFields = (formId: string) => {
  const dispatch = useDispatch();
  const loadForm = useCallback(
    () => dispatch(formsActions.fetchFormRequest(formId)),
    [dispatch, formId]
  );
  const { formfields: formFields } = useSelector(getFormInfo(formId)) || {};
  const { isLoading } = useAsync(loadForm);
  return useMemo(
    () => ({
      isLoading,
      formFields
    }),
    [isLoading, formFields]
  );
};

export const useHasMore = ({
  list,
  currentSearchText: initialCurrentSearchText = 'all'
}: {
  list: any[];
  currentSearchText?: string;
}) => {
  const [hasMore, setHasMore] = useState(1);
  const [currentSearchText, setCurrentSearchText] = useState(
    initialCurrentSearchText
  );
  useEffect(() => {
    if (list.length) setHasMore(prevHasMore => prevHasMore + 1);
  }, [list.length]);
  const handleFetchSuccess = useCallback(res => {
    setHasMore(prevHasMore => prevHasMore - 1);
    return res;
  }, []);
  return useMemo(
    () => ({
      hasMore,
      setHasMore,
      currentSearchText,
      setCurrentSearchText,
      handleFetchSuccess
    }),
    [
      hasMore,
      setHasMore,
      currentSearchText,
      setCurrentSearchText,
      handleFetchSuccess
    ]
  );
};

export const useLoadMore = ({
  operation: initialOperation,
  currentSearchText,
  list,
  setCurrentSearchText,
  setHasMore,
  perPage = 10
}: {
  operation: (...args: any[]) => void;
  currentSearchText: string;
  list: any[];
  setCurrentSearchText: (text: string) => void;
  setHasMore: (hasMore: number) => void;
  perPage?: number;
}) => {
  const [operation, setOperation] = useState(() => initialOperation);
  const loadMore = useCallback(() => {
    operation(currentSearchText, list.length + perPage);
  }, [operation, list.length, currentSearchText, perPage]);
  const handleOperation = useCallback(
    (nextOperation, searchText) => {
      setCurrentSearchText(searchText);
      setHasMore(1);
      setOperation(() => nextOperation);
    },
    [setCurrentSearchText, setHasMore]
  );
  return useMemo(
    () => ({
      loadMore,
      handleOperation
    }),
    [loadMore, handleOperation]
  );
};

const generateReference = (length = 15) => {
  let text = '';
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i += 1) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};

export const usePayStack = () => {
  const user = useSelector(getUser);
  const { openModal } = useContext(ModalContext);
  const { reload } = useContext(PageDataContext);
  const { show: showGlobalLoader, hide: hideGlobalLoader } = useContext(
    GlobalLoaderContext
  );
  const [loading, setLoading] = useState(false);
  const callback = useCallback(
    ({ successText }): PayStackCallback => ({ reference }) => {
      setLoading(true);
      showGlobalLoader?.();
      requester
        .post(
          '/send',
          {
            forminfo: {
              reference,
              settodefault: 1,
              successtext: successText || 'Card Added Successfully'
            },
            calltype: 'savecard'
          },
          { alertErrorMessage: true }
        )
        .then(
          (res: any) => {
            setLoading(false);
            hideGlobalLoader?.();
            if (res.status === 1) {
              openModal?.(MODALS.ALERT_MODAL, {
                title: 'Success',
                description: res.message || successText,
                onClose() {
                  if (reload) reload();
                  refreshPage();
                }
              });
            }
          },
          () => {
            setLoading(false);
          }
        );
    },
    [reload, openModal, hideGlobalLoader, showGlobalLoader]
  );
  const onClose = useCallback(() => {}, []);
  const pay = useCallback(
    args => () => {
      const {
        amount = 100,
        reference,
        prefix,
        title = '',
        subTitle = '',
        subtitle = '',
        type,
        wallet: incomingWallet = ''
      } = args || {};
      if (!user) {
        return;
      }
      let channels;
      if (type) {
        channels = [type];
      }
      const [wallet, walletId = ''] = incomingWallet.split('_');
      const handler = window.PaystackPop?.setup({
        email: user.email,
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY as string,
        amount: amount * 100,
        ref:
          reference ||
          `${prefix || PAYSTACK_REFERENCE_PREFIX}${generateReference()}`,
        channels,
        callback: callback(args),
        metadata: {
          wallet,
          id: walletId
        },
        onClose
      });
      openModal?.(MODALS.CONFIRMATION_MODAL, {
        headerTitle: title.replace(/\+/g, ' '),
        headerDescription: (subTitle || subtitle).replace(/\+/g, ' '),
        action: () => {
          handler?.openIframe?.();
        }
      });
    },
    [user, callback, onClose, openModal]
  );
  return useMemo(
    () => ({
      pay,
      loading
    }),
    [pay, loading]
  );
};

export const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  useEffect(() => {
    window.onresize = () => {
      setWindowHeight(window.innerHeight);
    };
  }, []);
  return windowHeight;
};

export const targetPageHeaders = {
  showleaderboard: {
    title: 'Leader Board',
    description: 'Top members of this challenge'
  },
  showmembers: {
    title: 'Members',
    description: 'Most recent members participating in this savings challenge'
  },
  showactivities: {
    title: 'Activities',
    description: 'Most recent activities of this savings target'
  },
  showtransactions: {
    title: 'Personal Transactions',
    description: 'Recent personal transactions in this savings target'
  }
};

export type Options = BaseTargetPageLinkData & {
  getCards?: (data: { [key: string]: any }) => ApiPageComponent[];
};

export const useTargetPageModalOptions = () => {
  const dispatch = useDispatch();
  const getTargetPageModalOptions = useCallback<
    (options: Options) => ModalOptions
  >(
    ({ loadType, targetID, getCards = () => [] }) => {
      const header = targetPageHeaders[loadType];
      return {
        headerTitle: header.title,
        headerDescription: header.description,
        getRawPageData: getTargetPageData(targetID, loadType),
        loadRawPageData() {
          return (dispatch(
            targetsActions.fetchTargetPageRequest({
              id: targetID,
              loadType
            })
          ) as unknown) as Promise<any>;
        },
        transformRawPageData(data: any) {
          return {
            components: getCards(data)
          };
        }
      };
    },
    [dispatch]
  );
  return useMemo(() => ({ getTargetPageModalOptions }), [
    getTargetPageModalOptions
  ]);
};

export const useTargetPage = () => {
  const match = useRouteMatch();
  const registerTargetPage = useCallback(
    (loadType, modalOptions, parent = match.url) => {
      const routeId = `TargetPage-${loadType}`;
      const targetPagePath = appendPageIdToUrl(
        parent,
        `target_page_${loadType}`
      );
      registerModalRoute(routeId, targetPagePath, modalOptions);
      return routeId;
    },
    [match.url]
  );
  return useMemo(() => ({ registerTargetPage }), [registerTargetPage]);
};

export const useTargetDataListPage = () => {
  const { getTargetPageModalOptions } = useTargetPageModalOptions();
  const { registerTargetPage } = useTargetPage();
  const match = useRouteMatch();
  const getTargetDataListPage = useCallback(
    ({ transactions, targetID, activeId, parent = match.url }) => {
      const { loadType } = transactions[activeId];
      const modalOptions = getTargetPageModalOptions({
        loadType,
        targetID,
        getCards(response: any) {
          const list = response[activeId];
          let mapFn;
          if (activeId === 'activities') {
            mapFn = ({
              cardtextraw,
              date,
              image,
              amount
            }: FlatCardResponse) => ({
              component: 'flat_card',
              imageLeft: image,
              containerClassName: 'mb-4',
              title: {
                text: cardtextraw,
                className: 'text-gray-900'
              },
              subTitle: {
                text: date,
                className: 'text-gray-600'
              },
              textRight: amount
                ? {
                    text: formatToCurrency(amount),
                    className: 'text-sm text-right'
                  }
                : undefined
            });
          } else if (activeId === 'personaltransactions') {
            mapFn = ({
              asgw_memo: title,
              asgw_value: amount,
              asgw_date: date
            }: TransactionResponse) => ({
              component: 'flat_card',
              iconLeft: {
                type: 'fa',
                name: 'circle',
                color: colors.green['600']
              },
              containerClassName: 'mb-4',
              title: {
                text: title,
                className: 'text-gray-900'
              },
              subTitle: {
                text: getRelativePeriod(date * 1000),
                className: 'text-gray-600'
              },
              textRight: amount
                ? {
                    text: formatToCurrency(amount),
                    className: 'text-sm text-right'
                  }
                : undefined
            });
          }
          if (list) {
            return list.map(mapFn);
          }
          return [];
        }
      });
      return registerTargetPage(loadType, modalOptions, parent);
    },
    [getTargetPageModalOptions, registerTargetPage, match.url]
  );
  return useMemo(() => ({ getTargetDataListPage }), [getTargetDataListPage]);
};

export const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [canUseClipBoard, setCanUseClipBoard] = useState(true);
  useEffect(() => {
    if (!navigator.clipboard) {
      setCanUseClipBoard(false);
      return;
    }
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: 'clipboard' })
        .then((result: PermissionStatus) => {
          if (result.state !== 'granted' && result.state !== 'prompt') {
            setCanUseClipBoard(false);
          }
        })
        .catch(() => {
          setCanUseClipBoard(true);
        });
    }
  }, []);
  const copyToClipboard = (text: string) => {
    if (!canUseClipBoard) {
      return Promise.reject(new Error());
    }
    return navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    });
  };
  return {
    copyToClipboard,
    canUseClipBoard,
    isCopied
  };
};

export const useScrollTogether = (
  el: HTMLElement | null,
  parentEl: HTMLElement | null
) => {
  useEffect(() => {
    const wheelListener = (evt: WheelEvent) => {
      if (el && parentEl) {
        const maxScrollTop = el.scrollHeight - el.clientHeight;
        const currentScrollTop = el.scrollTop;
        if (currentScrollTop === 0 || currentScrollTop === maxScrollTop) {
          parentEl.scrollBy(0, evt.deltaY);
        }
      }
    };
    if (el) el.addEventListener('wheel', wheelListener);
    return () => {
      if (el) el.removeEventListener('wheel', wheelListener);
    };
  }, [el, parentEl]);
};
